import { trackCardClick } from '@/lib/events'
import { navigate } from '@/app'
import { historyModalClosed, historyModalOpened } from '@/browser/history'
import {
  type ContentCard,
  type ContentDetail,
  type ContentLookup,
  type UpcomingItem,
  lookupContent,
  lookupUpcomingItem,
} from '@/lib/catalog'
import { formatPremiere } from '@/lib/dates'
import { clear, el, setClass, setText } from '@/lib/dom'
import { T } from '@/lib/i18n'
import { IMAGE_WIDTH, mediaUrl } from '@/lib/images'
import { toRemoteKey } from '@/lib/keys'
import { readSession } from '@/lib/session'
import { startMutedTrailer } from '@/lib/trailer'
import { trailerAllowed } from '@/lib/ageGate'
import { resumeEpisode } from '@/lib/continue'
import { cardFromDetail, isInWatchlist, toggleWatchlist } from '@/lib/watchlist'
import { episodeRow } from '@/browser/screens/detail'
import { episodeTitle } from '@/screens/player'
import { fullRuntime } from '@/ui/card'
import {
  ICON_CLOSE,
  ICON_FULLSCREEN,
  ICON_MUTED,
  ICON_PLAY,
  ICON_SOUND,
  iconBox,
  iconButton,
} from '@/browser/icons'
import { openWithAgeGate, webButton, webCard, webDescription, webRow } from '@/browser/ui'

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * FİLM MODALI — BRAUZER VARİANTI (tv.kinobox.az)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Sahib tələbi (2026-08-13): «vebtvdə film item klikdə tv projelər kimi açsın
 * modalı, android tvdə modal necə açılırsa veb tv versiyada da elə açılsın».
 *
 * ⚠⚠ BU, ƏVVƏLKİ QƏRARIN GERİ ALINMASIDIR. `browser/screens/detail.ts`-in
 *    başında yazılıb ki, brauzerdə detal SƏHİFƏDİR — səbəb iki idi:
 *      1) geri qayıdışı brauzer özü idarə edir (sıra `scrollLeft`-i qalır);
 *      2) MODAL DAXİLİNDƏ doğma sürüşmə TV brauzerində «tələ» yaradırdı —
 *         kənar qat sürüşür, iç qat yox.
 *    Birinci səbəb qüvvədən düşdü: modal ekran yığınına DÜŞMÜR, yəni arxadakı
 *    ekran ümumiyyətlə sökülmür və sıranın mövqeyi onsuz da qorunur (cihaz
 *    app-ındakı M8 ilə eyni).
 *    İkinci səbəb isə TEXNİKİ olaraq həll olunub: sürüşən qat İÇ div DEYİL,
 *    ÖRTÜYÜN ÖZÜDÜR (`.web-dm { overflow-y: auto }`), yəni iç-içə iki sürüşən
 *    qat yoxdur; arxadakı sənəd isə modal açıq olanda kilidlənir və bağlananda
 *    mövqe ƏL İLƏ geri qoyulur (`lockScroll`/`unlockScroll`).
 *
 * ⚠ ETALON ANDROID TV MODALIDIR (`TvContentDetailModal.kt`): üstdə treyler
 *   avtoplay, solda «İndi izlə», sağda tam ekran + səs, altında ad · meta ·
 *   janr çipləri · təsvir («Daha çox» ilə), ən altda oxşar filmlər sırası,
 *   sağ-yuxarıda «Bağla». Aktyor heyəti QƏSDƏN yoxdur (M5 qaydası).
 *
 * ⚠ BÖLÜM SİYAHISI MODALDA YOXDUR — üç TV səthində də belədir. Serialda «İndi
 *   izlə» İLK oynana bilən bölümü açır, bölüm dəyişmə isə oynadıcının ⚙
 *   panelindədir (`screens/player.ts` → «Bölüm» bölməsi).
 *
 * ⚠ FİLM SƏHİFƏSİ (`browser/screens/detail.ts`) SİLİNMİR: `detail` marşrutu
 *   ehtiyat yol kimi qalır (cihaz app-ındakı eyni qərar). Ona keçid yalnız
 *   modal qurula bilməyəndə olur.
 */

/** Oxşar sırada göstərilən kart sayı — cihaz modalındakı `MAX_SIMILAR` ilə eyni. */
const MAX_SIMILAR = 12

/** Meta sətrindəki janr çipi sayı — Android modalı ilə eyni (`take(4)`). */
const MAX_GENRE_TAGS = 4

/** «Rollarda» sətrindəki ad sayı — «Tezliklə» rejimində (cihaz modalı ilə eyni). */
const MAX_CAST_NAMES = 6

/** Bir dəfə qurulan qabığın düyünləri (məzmun hər açılışda yenilənir). */
interface WebModalUi {
  root: HTMLElement
  panel: HTMLElement
  art: HTMLElement
  video: HTMLVideoElement
  actions: HTMLElement
  watch: HTMLButtonElement
  /** «İndi izlə» / «Tezliklə» yazısı — düymənin İKONU qalsın deyə ayrıca düyündür. */
  watchLabel: HTMLElement
  /** «+ Sonra İzlə» / «✓ Siyahımdadır» — etiketi filmə görə boyanır. */
  later: HTMLButtonElement
  fullscreen: HTMLButtonElement
  sound: HTMLButtonElement
  soundOn: HTMLElement
  soundOff: HTMLElement
  close: HTMLButtonElement
  /*
    ⚠⚠ AŞAĞIDAKI DÜYÜN YALNIZ TƏRCÜMƏNİ YENİDƏN YAZMAQ ÜÇÜNDÜR (2026-08-30):
       `build()` singleton-dur (`if (ui) return ui`), yəni modalın DOM-u səhifənin
       ömrü boyu BİR DƏFƏ qurulur və dil dəyişəndə YENİDƏN QURULMUR — «Bağla»,
       tam ekran və səs etiketləri boot dilində donub qalırdı. `reset()` onları
       hər açılışda cari dildə yenidən yazır.
  */
  closeLabel: Text
  title: HTMLElement
  metaText: HTMLElement
  age: HTMLElement
  tags: HTMLElement
  premiere: HTMLElement
  desc: HTMLElement
  crew: HTMLElement
  actors: HTMLElement
  crewToggle: HTMLDetailsElement
  similar: HTMLElement
}

let ui: WebModalUi | null = null
let open = false

/** Hazırda göstərilən film. */
let currentSlug = ''
let detail: ContentDetail | null = null
/** Siyahıdakı kart — detal cavabı gələnə qədər ad/meta ondan yazılır. */
let seed: ContentCard | null = null
/** «Tezliklə» rejimi — detal sorğusu ATILMIR (dərc olunmamış kontentdə 404). */
let upcomingCard: UpcomingItem | null = null
/**
 * SON açılmış «Tezliklə» kartı — oynadıcıdan qayıdanda hansı modalın bərpa
 * olunacağını bu dəyər seçir (cihaz modalındakı `lastUpcoming` ilə eyni səbəb:
 * dərc olunmamış filmdə `getContent` 404 verir və istifadəçi tam ekrandan
 * çıxanda «açıla bilmədi» görərdi).
 */
let lastUpcoming: UpcomingItem | null = null
/** Sorğu nəsli — modal başqa filmlə doldurulubsa gecikmiş cavab atılır. */
let request = 0
let cancelTrailerSeek: (() => void) | null = null
let stopTrailer: (() => void) | null = null
/** Treyler bitib/sınıb — səs düyməsi onu SƏSLƏ yenidən başladır. */
let trailerOver = false
let restartWithSound: (() => void) | null = null
/** Modal açılmazdan əvvəlki sənəd sürüşməsi — bağlananda geri qoyulur. */
let savedScroll = 0
let sourceFocus: HTMLElement | null = null

/*
  2026-10-23: «İNDİ İZLƏ» DETAL GƏLMƏMİŞ DƏ BASILIR — basış nə edəcəyini bu vəziyyət seçir.
    NƏ TAPILDI (commit 3fcae49b): düymə detal cavabına qədər `disabled` idi, ona görə
    `focusFirst` fokusu «Bağla»ya qoyurdu və `applyDetail` onu geri qaytarmırdı; detal 6 s-də
    kəsiləndə (TV-də ilk sorğu 10 s-dən çox çəkir) düymə ƏBƏDİ sönük qalırdı.
    ƏVVƏL: pultla film açılmırdı — fokus «Bağla»da, «İndi izlə»yə çatmaq olmurdu.
    NİYƏ BELƏ: düymə həmişə aktiv və fokusdadır. Detal yoldadırsa basış YADDA SAXLANILIR və
    cavab gələn kimi oynadılır (serialın bölümü yalnız detaldan bilinir — 2026-10-11 səbəbi
    qalır). Detal alınmadısa: FİLM seed kartdan oynadılır (filmdə bölüm yoxdur), serial və
    naməlum növdə isə düymə «Yenidən cəhd et» olur və detalı təkrar istəyir.
*/
type DetailState = 'loading' | 'ready' | 'failed' | 'missing'
let detailState: DetailState = 'loading'
/** «İndi izlə» detal yolda ikən basılıb — cavab gələn kimi oynadılır. */
let playWanted = false
/** `focusFirst`-in son seçdiyi element — istifadəçi fokusu tərpətməyibsə yenidən seçilir. */
let autoFocused: HTMLElement | null = null

/** Modal açıqdırmı — qabıq klaviatura körpüsü üçün soruşur. */
export function isWebDetailOpen(): boolean {
  return open
}

/**
 * Kartdan modal açır.
 *
 * ⚠ YAŞ QAPISI BURADADIR, ÇAĞIRIŞ YERLƏRİNDƏ YOX: modal ana ekran, kəşf,
 *   axtarış, siyahı, «Tezliklə» və oxşarlar sırasından açılır — altı yerə eyni
 *   şərti yazsaydıq biri unudulanda həmin səth səssizcə qapısız qalardı
 *   (cihaz modalındakı eyni qərar).
 * ⚠ QAPI AÇILIŞDAN ƏVVƏLDİR: 18+ kartda modal AÇILMAMALIDIR, çünki içindəki
 *   treyler dərhal oynayır (MYTasks 530).
 */
export function openWebDetail(card: ContentCard): void {
  trailerResumeAt = null
  const opening = ++request
  openWithAgeGate(card, () => {
    if (opening !== request) return
    openGated(card)
  })
}

/**
 * Qapıdan keçmiş kart.
 * ⚠ PLANA SALINMIŞ FİLM ADİ MODAL AÇMIR (MYTasks 709): eyni film həm kolleksiya
 *   sırasında, həm «Tezliklə» ekranında olur. Bayraqsız halda `content/{slug}` 404
 *   alır və istifadəçi «açıla bilmədi» görür.
 */
function openGated(card: ContentCard): void {
  if (card.isComingSoon) {
    enterUpcomingFromCard(card)
    return
  }
  enter(card.slug, card)
}

/**
 * ID ilə gələn kontent (yeniləmə · ünvan çubuğu · brauzerin «İrəli»si) — YAŞ QAPISI İLƏ.
 *
 * 2026-10-23: `#/film/<slug>` bərpası qapını ötürürdü.
 *   NƏ TAPILDI: `browser/history.ts` → `restoreFromLocation` modal körpüsünün `openBySlug`-unu
 *   çağırırdı, o isə `openWebDetailBySlug` idi — oynadıcıdan QAYIDIŞ üçün yazılmış, qapısız yol.
 *   ƏVVƏL: 18+ filmin ünvanı ilə səhifəni yeniləyən (və ya linki yapışdıran) istifadəçi
 *   modalı açırdı və treyler dərhal oynayırdı (qırmızı xətt 3a, MYTasks 530).
 *   NİYƏ BELƏ: yaş həddi yalnız detaldadır — əvvəl detal alınır, sonra qapı, sonra modal
 *   (alınmış detalla, ikinci sorğusuz). Detal alınmasa modal AÇILMIR: altdakı ekran qalır,
 *   18+ treyleri yoxlanmamış oynatmaqdansa açmamaq doğrudur.
 */
export function openWebDetailGated(slug: string): void {
  trailerResumeAt = null
  const asked = ++request
  void lookupContent(slug).then((result) => {
    if (asked !== request) return
    const found = result.detail
    if (!found) {
      // 404 — plana salınmış film ola bilər: onun öz kartı yaş həddini daşıyır.
      if (!result.notFound) return
      void lookupUpcomingItem(slug).then((upcoming) => {
        if (asked !== request || !upcoming.item) return
        const item = upcoming.item
        openWithAgeGate(item, () => {
          if (asked === request) enterUpcoming(item)
        })
      })
      return
    }
    const card = cardOf(found)
    openWithAgeGate(card, () => {
      if (asked !== request) return
      enter(slug, card, result)
    })
  })
}

/** Detaldan kart forması — qapı və «seed» müqaviləsi üçün. */
function cardOf(found: ContentDetail): ContentCard {
  return {
    id: found.id,
    slug: found.slug,
    title: found.title,
    kind: found.kind,
    ageLimit: found.ageLimit,
    year: found.year,
    posterUrl: found.posterUrl,
    rating: found.rating,
    durationMinutes: found.durationMinutes,
    isComingSoon: found.isComingSoon,
  }
}

/**
 * «Tezliklə» kartından modal açır — YÜNGÜL forma (523 müqaviləsi):
 * treyler + məlumat, «İzlə» və oxşarlar yoxdur.
 */
export function openWebUpcoming(item: UpcomingItem): void {
  trailerResumeAt = null
  const opening = ++request
  openWithAgeGate(item, () => { if (opening === request) enterUpcoming(item) })
}

/**
 * HERO-NUN «İZLƏ»Sİ — modalın «İndi izlə»si ilə EYNİ yol.
 *
 * 2026-10-23: hero oynadıcını yalnız `contentId` ilə açırdı.
 *   NƏ TAPILDI (`browser/screens/home.ts`): serial hero-da `episodeId` getmirdi (bilet 404 →
 *   «video hazır deyil»; hovuzda `kuklaxanap-butun-bolumler` var), «Tezliklə» yoxlanılmırdı,
 *   sonluq qatının məlumatı (oxşarlar · `outroSeconds`) ötürülmürdü.
 *   ƏVVƏL: modalda düzgün işləyən axın hero-da ikinci, yarımçıq nüsxə idi.
 *   NİYƏ BELƏ: qapı · «Tezliklə» · giriş · bölüm seçimi · marşrut bir yerdədir
 *   (`launchPlayer`) — cihaz hero-sundakı «tək həqiqət mənbəyi» qərarı (`screens/home.ts`).
 *   Detal alınmasa film seed kartdan oynadılır; serial/404/«Tezliklə» isə modala düşür,
 *   orada izah və «Yenidən cəhd et» var (ölü klik yoxdur).
 * ⚠ `request` NƏSLİ ORTAQDIR: istifadəçi gözləmə vaxtı başqa kart açsa və ya ekranı dəyişsə
 *   (`shell.ts` → `applyRoute` → `closeWebDetail`) gec gələn cavab oynadıcı açmır.
 * @param busy detal gözlənilərkən hero düyməsinə «Açılır…» yazmaq üçün.
 * @param fromHero oynadıcıdan çıxış ANA EKRANA olsun, modala yox (bax `launchPlayer`).
 */
export function playWebContent(card: ContentCard, busy?: (on: boolean) => void, fromHero?: boolean): void {
  trailerResumeAt = null
  const asked = ++request
  openWithAgeGate(card, () => {
    if (asked !== request) return
    if (card.isComingSoon) {
      enterUpcomingFromCard(card)
      return
    }
    if (!readSession()) {
      navigate({ name: 'qr' })
      return
    }
    if (busy) busy(true)
    void lookupContent(card.slug).then((result) => {
      if (busy) busy(false)
      if (asked !== request) return
      const found = result.detail
      if (found && found.isComingSoon !== true) {
        launchPlayer(card.slug, found, card, fromHero)
        return
      }
      if (!found && !result.notFound && card.kind === 'movie') {
        launchPlayer(card.slug, null, card, fromHero)
        return
      }
      enter(card.slug, card, result)
    })
  })
}

/**
 * Oynadıcıdan qayıdış (`app.ts` → `backToDetail` → `setDetailOpener`).
 *
 * ⚠ YAŞ QAPISI ÇAĞIRILMIR: istifadəçi bu filmi onsuz da açıb (qapıdan keçib) və
 *   indi yalnız geri qayıdır. Yeni bir kontent açılmır.
 */
export function openWebDetailBySlug(slug: string, trailerAt?: number): void {
  // ⚠ MODALDAN ƏVVƏL YAZILIR — treyler modal qurulan kimi başlayır.
  trailerResumeAt = typeof trailerAt === 'number' ? trailerAt : null
  if (lastUpcoming && lastUpcoming.slug === slug) {
    enterUpcoming(lastUpcoming)
    return
  }
  enter(slug, null)
}

/** Modalı bağlayır (✕ · Esc · fon kliki). Arxadakı ekrana TOXUNULMUR. */
export function closeWebDetail(): void {
  trailerResumeAt = null
  request += 1
  playWanted = false
  if (cancelTrailerSeek) { cancelTrailerSeek(); cancelTrailerSeek = null }
  const u = ui
  if (!open || !u) return
  open = false
  upcomingCard = null
  trailerOver = false
  restartWithSound = null
  if (stopTrailer) {
    stopTrailer()
    stopTrailer = null
  }
  u.root.hidden = true
  if (sourceFocus && document.documentElement.contains(sourceFocus)) sourceFocus.focus()
  sourceFocus = null
  unlockScroll()
  // Ekranın öz ünvanı geri qayıdır (`browser/history.ts`).
  historyModalClosed()
}

// ── Açılış ─────────────────────────────────────────────────────────────────

/**
 * Adi (dərc olunmuş) film — detal sorğusu ilə.
 * @param preloaded çağıran detalı artıq alıbsa (hero · ünvan bərpası) — ikinci sorğu atılmır.
 */
function enter(slug: string, card: ContentCard | null, preloaded?: ContentLookup): void {
  // Kart klik hadisəsi (BR 27.4) — açılışa mane olmur, arxa planda gedir.
  trackCardClick(slug, 'webtv')
  const u = build()

  upcomingCard = null
  if (!lastUpcoming || lastUpcoming.slug !== slug) lastUpcoming = null

  currentSlug = slug
  detail = null
  seed = card
  playWanted = false
  detailState = 'loading'
  request += 1
  const asked = request

  reset(u, false)
  show(u)
  // Ünvan `#/film/slug` olur — yeniləmə modalı bərpa edir (`browser/history.ts`).
  historyModalOpened(slug)

  /*
    ⚠ TREYLER AÇILIŞ ANINDA BAŞLAYIR — detal cavabı GÖZLƏNMİR (sahib tələbi
      2026-08-11: «film modal açılanda birbaşa trailer başlasın»). Kart
      cavabında treyler sahəsi yoxdur, amma `…/{slug}/trailer` ucu var və
      detala ehtiyac duymur (`startMutedTrailer` mənbə boş olanda ora gedir).
    ⚠ Ehtiyat kadr SEED kartının afişasıdır — başlıq kadrı hələ məlum deyil.
  */
  const seedArt = card ? mediaUrl(card.posterUrl, IMAGE_WIDTH.backdrop) : null
  const known = preloaded && preloaded.detail ? preloaded.detail : null
  /*
    2026-10-25, kod oxunuşu: treyler `trailerAllowed`-dən keçir. Modal klik qapısından
      (`openWithAgeGate` → `evaluateAge`) keçir, o isə girişsiz baxana 18+ üçün də «allowed»
      deyir — ƏVVƏL 18+ treyler girişsiz modalda öz-özünə oynayırdı. Yaş həddi bilinmirsə
      (oynadıcıdan seed-siz qayıdış) treyler detal gələndə `applyDetail`-də başlayır.
  */
  const remembered = returnLimit && returnLimit.slug === slug ? returnLimit.ageLimit : undefined
  const knownLimit = known ? known.ageLimit : card ? card.ageLimit || null : remembered
  if (knownLimit !== undefined && trailerAllowed(knownLimit)) {
    beginTrailer(u, known ? known.trailerUrl : null, slug, seedArt)
  } else if (seedArt) {
    u.art.style.backgroundImage = 'url("' + seedArt + '")'
  }

  if (preloaded) {
    settleDetail(u, slug, preloaded)
    return
  }
  loadDetail(u, slug, asked)
}

function loadDetail(u: WebModalUi, slug: string, asked: number): void {
  detailState = 'loading'
  void lookupContent(slug).then((result) => {
    if (!open || asked !== request) return
    settleDetail(u, slug, result)
  })
}

/** Detal cavabının üç halı: uğur · 404 · şəbəkə xətası/taymaut. */
function settleDetail(u: WebModalUi, slug: string, result: ContentLookup): void {
  const found = result.detail
  if (found) {
    detail = found
    detailState = 'ready'
    applyDetail(u, found)
    const wanted = playWanted
    playWanted = false
    if (wanted && found.isComingSoon !== true) playFilm()
    return
  }

  if (result.notFound) {
    /*
      ⚠ 404 = ya plana salınmış film (status=2, kart bayrağı yoxdur/köhnədir), ya da bu
        profildə bağlı/silinmiş kontent — `browser/screens/detail.ts`-dəki eyni sıra.
    */
    const asked = request
    void lookupUpcomingItem(slug).then((upcoming) => {
      if (!open || asked !== request) return
      if (upcoming.item) {
        enterUpcoming(upcoming.item)
        return
      }
      detailState = 'missing'
      playWanted = false
      fillDescription(u, T('Məlumat tapılmadı.'))
      u.watch.disabled = true
      refocus(u)
    })
    return
  }

  // Dead-end yoxdur (§6): pəncərə izahlıdır, «İndi izlə» isə seed-dən oynadır və ya təkrar soruşur.
  detailState = 'failed'
  fillDescription(u, T('Bu məzmun açıla bilmədi'))
  const wanted = playWanted
  playWanted = false
  setText(u.watchLabel, canPlayFromSeed() ? T('İndi izlə') : T('Yenidən cəhd et'))
  if (wanted && canPlayFromSeed()) playFilm()
}

/**
 * Oynadıcını bu modal açıbsa filmin yaş həddi — seed kartsız qayıdışda (`openWebDetailBySlug`)
 * treyler detalı gözləmədən `trailerAllowed` ilə başlaya bilsin (2026-10-25).
 */
let returnLimit: { slug: string; ageLimit: number | null } | null = null

/** Detalsız oynatma YALNIZ filmdə olur — serialın bölümü yalnız detaldan bilinir. */
function canPlayFromSeed(): boolean {
  return Boolean(seed && seed.id && seed.kind === 'movie')
}

/*
  2026-10-23: «TEZLİKLƏ» KARTI KLİK ANINDA AÇILIR — kartın öz məlumatı ilə.
    NƏ TAPILDI: modal `upcoming/{slug}` cavabını (6 s-ə qədər) gözləyirdi — klikdən sonra
    ekranda HEÇ NƏ dəyişmirdi; taymautda isə `content/{slug}`-a düşürdü, o da plana
    salınmış (status=2) kontentdə 404 verir → «Bu məzmun açıla bilmədi».
    ƏVVƏL: ölü klik, sonra xəta mətni.
    NİYƏ BELƏ: ad · il · yaş · afişa kartdadır — modal dərhal qurulur, təsvir · heyət ·
    premyera · treyler cavab gələndə yazılır. Detal ucuna YALNIZ server «bu artıq Tezliklə
    deyil» (404) deyəndə keçilir (kart bayrağı köhnədir); şəbəkə xətasında modal
    «Tezliklə»də qalır və izah yazılır.
*/
function enterUpcomingFromCard(card: ContentCard): void {
  const u = build()
  enterUpcoming(upcomingFromCard(card), true)
  fillDescription(u, T('Yüklənir…'))
  const asked = request
  void lookupUpcomingItem(card.slug).then((result) => {
    if (!open || asked !== request) return
    if (result.item) {
      applyUpcoming(u, result.item)
      return
    }
    if (result.notFound) {
      enter(card.slug, card)
      return
    }
    fillDescription(u, T('Film açıla bilmədi. Bir azdan yenidən yoxlayın.'))
  })
}

function upcomingFromCard(card: ContentCard): UpcomingItem {
  return Object.assign({}, card, {
    rating: null,
    publishAt: null,
    releaseDate: null,
    description: null,
    trailerUrl: null,
    backdropUrl: card.backdropUrl || null,
  }) as UpcomingItem
}

/**
 * «Tezliklə» rejimi — şəbəkə sorğusu yoxdur, hər şey kartın öz cavabındadır.
 * @param provisional kart məlumatı ilə ilkin forma — treyler tam cavabla başlayır.
 */
function enterUpcoming(item: UpcomingItem, provisional?: boolean): void {
  const u = build()

  upcomingCard = item
  lastUpcoming = item
  currentSlug = item.slug
  detail = null
  seed = item
  playWanted = false
  detailState = 'missing'
  request += 1

  reset(u, true)
  show(u)
  historyModalOpened(item.slug)
  paintUpcoming(u, item, !provisional)
}

/** Tam «Tezliklə» cavabı ilkin formanın üstünə yazılır (modal yenidən açılmır). */
function applyUpcoming(u: WebModalUi, item: UpcomingItem): void {
  upcomingCard = item
  lastUpcoming = item
  seed = item
  paintUpcoming(u, item, true)
  // Treyler mənbəyi indi məlumdur — tam ekran düyməsi çıxıbsa avtomatik fokus ora keçir.
  refocus(u)
}

function paintUpcoming(u: WebModalUi, item: UpcomingItem, withTrailer: boolean): void {
  setText(u.title, item.title)
  paintMeta(u, null)

  const premiere = formatPremiere(item)
  if (premiere) {
    setText(u.premiere, T('Premyera') + ': ' + premiere)
    u.premiere.hidden = false
  }

  fillDescription(u, item.description)
  fillCrew(u, item)

  const art = mediaUrl(item.backdropUrl || item.posterUrl, IMAGE_WIDTH.backdrop)
  // 2026-10-25: «Tezliklə» treyleri də `trailerAllowed`-dən keçir (adi modaldakı izah).
  const allowed = trailerAllowed(item.ageLimit)
  if (withTrailer && allowed) beginTrailer(u, item.trailerUrl, item.slug, art)
  else if (art) u.art.style.backgroundImage = 'url("' + art + '")'

  // ⚠ «İzlə» YOXDUR (523): kontent dərc olunmayıb, bilet onsuz da verilmir.
  //   Tam ekran düyməsi QALIR — treyler mənbəyi var (yaşa görə oynaya bilmirsə yox).
  u.watch.hidden = true
  u.fullscreen.hidden = !item.trailerUrl || !allowed
}

/** Detal cavabı gələndə: ad · meta · təsvir · oxşarlar · treyler mənbəyi. */
function applyDetail(u: WebModalUi, found: ContentDetail): void {
  u.watch.disabled = found.isComingSoon === true
  setText(u.watchLabel, T('İndi izlə'))
  setText(u.title, found.title)
  paintMeta(u, found)
  // Seed kart olmadan açılan modalda (`openWebDetailBySlug`) `id` yalnız indi
  // məlum olur — etiket yenidən boyanır.
  paintLater(u)
  fillDescription(u, found.description || T('Təsvir hələ əlavə olunmayıb.'))
  // ⚠ SERİALDA BÖLÜMLƏR, FİLMDƏ OXŞARLAR — bir-birini əvəz edir (izahı `fillBottom`-da).
  fillBottom(u, found)

  // ⚠ OYNATMA BAĞLIDIRSA DÜYMƏ SÖNÜR (MYTasks 499) — «Tezliklə» filmi kart
  //   bayrağı olmadan da bu yola düşə bilir.
  if (found.isComingSoon === true) {
    setText(u.watchLabel, T('Tezliklə'))
    u.watch.disabled = true
  }

  // 2026-10-25: fon treyleri, səslə təkrar və tam ekran `trailerAllowed`-dən keçir.
  const allowed = trailerAllowed(found.ageLimit)
  u.fullscreen.hidden = !found.trailerUrl || !allowed

  /**
   * ⚠ AÇILIŞDA BAŞLADILAN TREYLER KƏSİLMİR: oynamağa başlamış video sıfırdan
   *   yüklənsəydi istifadəçi bunu «treyler ilişdi» kimi görərdi. Yalnız ehtiyat
   *   kadr (başlıq kadrı) dəqiqləşdirilir. Detalın yaş həddi treyleri qadağan edirsə
   *   (kartın həddi köhnə idi) dayandırılır.
   */
  const art = mediaUrl(found.backdropUrl || found.posterUrl, IMAGE_WIDTH.backdrop)
  if (!allowed) {
    if (stopTrailer) {
      stopTrailer()
      stopTrailer = null
    }
    trailerOver = true
    u.video.className = 'web-dm__trailer'
    if (art) u.art.style.backgroundImage = 'url("' + art + '")'
  } else if (!stopTrailer) beginTrailer(u, found.trailerUrl, found.slug, art)
  else if (art && trailerOver) u.art.style.backgroundImage = 'url("' + art + '")'

  // Treyler bitəndən sonra səs düyməsi onu SƏSLƏ yenidən başladır — «heç nə
  // etməyən düymə qalmasın» qaydası (Android modalı, 522).
  restartWithSound = allowed
    ? () => {
        beginTrailer(u, found.trailerUrl, found.slug, art)
        u.video.muted = false
        paintSound(u)
      }
    : null

  // Düymə sönübsə (Tezliklə) və ya istifadəçi fokusu tərpətməyibsə fokus yenidən seçilir.
  refocus(u)
}

// ── Məzmun ─────────────────────────────────────────────────────────────────

/** Yeni film gələnə qədər köhnəsinin izi qalmasın. */
function reset(u: WebModalUi, upcoming: boolean): void {
  if (cancelTrailerSeek) { cancelTrailerSeek(); cancelTrailerSeek = null }
  if (stopTrailer) {
    stopTrailer()
    stopTrailer = null
  }
  trailerOver = false
  restartWithSound = null
  u.video.className = 'web-dm__trailer'
  u.video.muted = true
  paintSound(u)
  u.art.style.backgroundImage = ''

  setText(u.title, seed ? seed.title : T('Yüklənir…'))
  setText(u.metaText, '')
  u.age.hidden = true
  clear(u.tags)
  u.premiere.hidden = true
  u.crew.hidden = true
  u.actors.hidden = true
  u.crewToggle.hidden = true
  u.crewToggle.open = false
  clear(u.desc)
  clear(u.similar)

  setText(u.watchLabel, T('İndi izlə'))
  /*
    ⚠⚠ SABİT ETİKETLƏR HƏR AÇILIŞDA YENİDƏN TƏRCÜMƏ OLUNUR (2026-08-30) — cihaz
       modalındakı eyni düzəliş. ÖLÇÜLDÜ: üst zolaqdan dil dəyişdirildikdən sonra
       səhifə yeni dildə qurulurdu, modalın «Bağla» yazısı və iki yalnız-ikon
       düyməsinin `aria-label`/`title` dəyəri isə boot dilində qalırdı — ekran
       oxuyucusu və `title` ipucu səhifənin qalanından FƏRQLİ dil deyirdi.
    ⚠ İKONA TOXUNULMUR: mətn ayrıca düyündədir (`closeLabel`), `aria-label`/`title`
      isə atributdur — SVG yenidən parse olunmur.
  */
  u.closeLabel.nodeValue = T('Bağla')
  u.fullscreen.setAttribute('aria-label', T('Tam ekran'))
  u.fullscreen.title = T('Tam ekran')
  u.sound.setAttribute('aria-label', T('Səs'))
  u.sound.title = T('Səs')
  // 2026-10-23: düymə AKTİVDİR — detal gəlməmiş basış gözlədilir (izah `detailState`-də).
  //   2026-10-11-in səbəbi (serialın bölümü detalsız bilinmir) `playFilm`-də qorunur.
  u.watch.disabled = false
  u.watch.hidden = upcoming
  // «Sonra İzlə» etiketi kartın `id`-si ilə DƏRHAL boyanır (indeks sinxrondur —
  // `lib/watchlist.ts`); «Tezliklə» rejimində düymə görünmür, çünki dərc
  // olunmamış kontent siyahıya salına bilməz (uc `contentId` tanımır).
  paintLater(u)
  u.later.hidden = upcoming
  // Mənbə məlum olana qədər gizlidir: boş oynadıcı ekranı açan düymə qalmasın.
  u.fullscreen.hidden = true
  setClass(u.root, 'is-upcoming', upcoming)
}

/**
 * Meta sətri: il · müddət · ★ reytinq, sonra yaş nişanı və janr çipləri.
 * Sıra cihaz modalı və Android TV ilə eynidir.
 */
function paintMeta(u: WebModalUi, found: ContentDetail | null): void {
  const card = seed
  const parts: string[] = []

  const year = found ? found.year : card ? card.year : null
  if (year) parts.push(String(year))

  // ⚠ Müddət TAM formadadır («1 saat 42 dəq») — dörd səthdə eyni qayda.
  const duration =
    found && found.durationMinutes ? found.durationMinutes : card && card.durationMinutes ? card.durationMinutes : null
  if (duration) parts.push(fullRuntime(duration))

  // ⚠ «Tezliklə» rejimində reytinq YAZILMIR (523: film çıxmayıb).
  const rating = upcomingCard ? null : found ? found.rating : card ? card.rating : null
  if (rating) parts.push('★ ' + rating.toFixed(1))

  setText(u.metaText, parts.join('  ·  '))

  const age = found ? found.ageLimit : card ? card.ageLimit : null
  if (age) {
    setText(u.age, age + '+')
    u.age.hidden = false
  } else {
    u.age.hidden = true
  }

  // Janrlar ÇİPDİR, meta mətninə calanmış sətir DEYİL (Android `TvTag` sırası).
  const names: string[] = []
  if (upcomingCard) {
    const genres = upcomingCard.genreNames
    if (genres) {
      for (let i = 0; i < genres.length && names.length < 3; i++) {
        const name = genres[i]
        if (name) names.push(name)
      }
    }
    const moods = upcomingCard.moodNames
    if (moods && moods[0]) names.push(moods[0])
  } else if (found) {
    for (let i = 0; i < found.genres.length && names.length < MAX_GENRE_TAGS; i++) {
      const name = found.genres[i]
      if (name) names.push(name)
    }
  }
  clear(u.tags)
  for (let i = 0; i < names.length; i++) u.tags.appendChild(el('span', 'web-dm__tag', names[i] as string))
}

/**
 * Təsvir — dörd sətirdən sonra «Daha çox» (ortaq `webDescription`).
 * ⚠ Blok hər açılışda YENİDƏN qurulur: düymənin lazım olub-olmadığı MƏTNDƏN və
 *   pəncərənin enindən asılıdır və ölçmə yalnız qurulma anında edilir.
 */
function fillDescription(u: WebModalUi, text: string | null | undefined): void {
  clear(u.desc)
  if (!text) return
  u.desc.appendChild(webDescription(text, 'web-dm__descbody'))
}

/** Heyət sətri — YALNIZ «Tezliklə» rejimində (adi modalda M5 qaydası: yoxdur). */
function fillCrew(u: WebModalUi, item: UpcomingItem): void {
  const directors = item.directorNames || []
  const cast = item.castNames || []
  if (!directors.length && !cast.length) return

  clear(u.crew)
  if (directors.length) {
    u.crew.appendChild(el('b', '', T('Rejissor')))
    u.crew.appendChild(document.createTextNode(directors.join(', ') + '  '))
  }
  if (cast.length) {
    setText(u.actors, T('Rollarda: ') + cast.slice(0, MAX_CAST_NAMES).join(', '))
    u.actors.hidden = false
  }
  u.crew.hidden = !directors.length
  u.crewToggle.hidden = false
}

/**
 * Oxşar filmlər sırası.
 * ⚠ KART KLİKİ MODALI ƏVƏZ EDİR, yığın yığmır (M6) — geri düyməsi beş dəfə
 *   basılmalı olmasın.
 */
/**
 * Modalın alt sırası: SERİALDA bölümlər, FİLMDƏ oxşarlar.
 *
 * ⚠⚠ ƏVVƏL SERİALDA DA «Oxşar filmlər» ÇƏKİLİRDİ (2026-09-07-də ölçüldü).
 *    NƏ ÖLÇÜLDÜ (yerli veb TV, canlı API): «Kuklaxana bütün bölümlər» kartına klik
 *    edildi — modalda 7 bölümün HEÇ BİRİ yox idi, `web-ep` sinifli 0 element
 *    çəkildi, aşağıda isə «Oxşar filmlər» sırası dururdu. API-də bölümlər var
 *    (7 ədəd, hər birinin `thumbnailUrl`-u ilə).
 *    ƏVVƏL NƏ OLURDU: `fillSimilar` şərtsiz çağırılırdı; bölüm sətri yalnız film
 *    SƏHİFƏSİNDƏ (`screens/detail.ts`) vardı, modalda isə heç vaxt. Kartdan
 *    açılan serialda istifadəçinin bölüm seçmək yolu QALMIRDI.
 *    NİYƏ MƏHZ BU HƏLL: qayda üç TV səthində artıq belədir (Apple TV
 *    `ContentDetailModal.bottomRow`, cihaz variantı `ui/detailModal.ts`) — veb TV
 *    tək istisna idi. Sətir `screens/detail.ts`-dəki EYNİ qurucudan gəlir, yəni
 *    kadr + sol-aşağı nişan ikinci dəfə yazılmır.
 */
function fillBottom(u: WebModalUi, detail: ContentDetail): void {
  if (detail.episodes.length) {
    clear(u.similar)
    const rows = el('div', 'web-dm__eps')
    for (let i = 0; i < detail.episodes.length; i++) {
      const episode = detail.episodes[i]
      if (!episode) continue
      rows.appendChild(episodeRow(detail, episode))
    }
    const block = el('div')
    const head = el('h2', 'web-row__title', T('Bölümlər'))
    head.style.margin = '20px 0 12px 0'
    block.appendChild(head)
    block.appendChild(rows)
    u.similar.appendChild(block)
    return
  }
  fillSimilar(u, detail.similar)
}

function fillSimilar(u: WebModalUi, items: ContentCard[]): void {
  clear(u.similar)
  if (!items.length) return
  const cards: HTMLElement[] = []
  for (let i = 0; i < items.length && i < MAX_SIMILAR; i++) {
    const item = items[i] as ContentCard
    cards.push(webCard(item, (picked) => openWebDetail(picked)))
  }
  u.similar.appendChild(webRow(T('Oxşar filmlər'), cards))
}

// ── Treyler ────────────────────────────────────────────────────────────────

/**
 * Fon treylerini başladır (səssiz, dövr etmir — üç TV səthində eyni qayda).
 * @param art treyler bitəndə/alınmayanda qoyulacaq kadr (afişa EHTİYATDIR).
 */
/**
 * OYNADICI EKRANINDAN QAYIDANDA treylerin davam edəcəyi saniyə.
 * İzah cihaz variantındadır (`ui/detailModal.ts` → `trailerResumeAt`).
 */
let trailerResumeAt: number | null = null

/** Yadda saxlanmış mövqeni oxuyur və dərhal unutdurur. */
function consumeTrailerResume(): number {
  const at = trailerResumeAt
  trailerResumeAt = null
  return at !== null && isFinite(at) && at > 0 ? at : 0
}

/** Treyler ilk kadrı verəndə mövqeyə tullanır (izah cihaz variantındadır). */
function seekTrailer(video: HTMLVideoElement, at: number): void {
  if (cancelTrailerSeek) { cancelTrailerSeek(); cancelTrailerSeek = null }
  if (at <= 0) return
  const apply = (): void => {
    if (isFinite(video.duration) && video.duration > 0 && at >= video.duration - 1) return
    try {
      video.currentTime = at
    } catch {
      // Axtarış hazır deyil — treyler başdan oynayır.
    }
  }
  if (video.readyState >= 1) apply()
  else {
    // ⚠ `{ once: true }` Chrome 55+-dır — Chrome 53 obyekti `capture=true` sayır və `once`-u
    //   atır, `apply` hər `loadedmetadata`-da təkrar işləyirdi (2026-09-26). Əl ilə silinir.
    const onMeta = (): void => {
      video.removeEventListener('loadedmetadata', onMeta)
      apply()
    }
    video.addEventListener('loadedmetadata', onMeta)
    cancelTrailerSeek = () => video.removeEventListener('loadedmetadata', onMeta)
  }
}

function beginTrailer(u: WebModalUi, asset: string | null, slug: string, art: string | null): void {
  if (stopTrailer) {
    stopTrailer()
    stopTrailer = null
  }
  trailerOver = false
  stopTrailer = startMutedTrailer(
    u.video,
    asset,
    slug,
    () => {
      trailerOver = false
      u.video.className = 'web-dm__trailer is-ready'
      seekTrailer(u.video, consumeTrailerResume())
    },
    () => {
      trailerOver = true
      u.video.className = 'web-dm__trailer'
      if (art) u.art.style.backgroundImage = 'url("' + art + '")'
    },
  )
}

function toggleSound(u: WebModalUi): void {
  if (trailerOver && restartWithSound) {
    restartWithSound()
    return
  }
  u.video.muted = !u.video.muted
  paintSound(u)
}

function paintSound(u: WebModalUi): void {
  u.soundOff.hidden = !u.video.muted
  u.soundOn.hidden = u.video.muted
}

// ── Aksiyalar ──────────────────────────────────────────────────────────────

/**
 * «İndi izlə» — BİRBAŞA oynadıcı (film səhifəsi açılmır).
 *
 * ⚠ Giriş yoxdursa QR ekranına gedir və treyler AÇILMIR (MYTasks 214).
 * ⚠ SERİALDA BİRİNCİ OYNANA BİLƏN BÖLÜM: serialın öz `cdn_asset_id`-si boşdur,
 *   bölümsüz bilet «video hazır deyil» qaytarardı.
 * ⚠ MODAL ƏVVƏLCƏ BAĞLANIR: treyler dayanmasa oynadıcı ilə İKİ video paralel
 *   dekod olunar (köhnə TV brauzerində birbaşa donma).
 */
function playFilm(): void {
  const u = ui
  if (!u) return

  if (!readSession()) {
    closeWebDetail()
    navigate({ name: 'qr' })
    return
  }

  const slug = currentSlug
  if (detailState === 'ready' && detail) {
    if (detail.isComingSoon === true) return
    const found = detail
    const card = seed
    closeWebDetail()
    launchPlayer(slug, found, card)
    return
  }

  // Detal yoldadır — basış yadda saxlanılır, `settleDetail` cavabla oynadır.
  if (detailState === 'loading') {
    playWanted = true
    setText(u.watchLabel, T('Yüklənir…'))
    return
  }

  if (detailState === 'failed') {
    if (canPlayFromSeed()) {
      const card = seed
      closeWebDetail()
      launchPlayer(slug, null, card)
      return
    }
    // Serial/naməlum növ: detal YENİDƏN istənir, cavab gələn kimi oynadılır.
    playWanted = true
    setText(u.watchLabel, T('Yüklənir…'))
    request += 1
    loadDetail(u, slug, request)
  }
}

/**
 * Oynadıcı marşrutu — modalın «İndi izlə»si və hero-nun «İzlə»si EYNİ funksiyanı çağırır.
 *
 * ⚠ SERİALDA BİRİNCİ OYNANA BİLƏN BÖLÜM: serialın öz `cdn_asset_id`-si boşdur,
 *   bölümsüz bilet «video hazır deyil» qaytarardı.
 * ⚠ `found` YOXDURSA (detal alınmadı) yalnız FİLM gəlir (`canPlayFromSeed`) —
 *   bilet alınmasa oynadıcı treylerə degrade edir.
 * ⚠⚠ HERO-DAN AÇILAN FİLM ÇIXIŞDA ANA EKRANA QAYIDIR (sahib tələbi 2026-10-25, MYTasks 1118:
 *    «modaldan full screen edilibsə modala qayıtmalıdır, pagedən edilibsə pagedə, herodan
 *    edilibsə elə ana səhifəyə qayıtmalıdır və bu tv projedə də belə olmalıdır»).
 *    NƏ TAPILDI (kodda): hero «İzlə»si bu funksiyaya `fromHero`-suz gəlirdi, oynadıcının
 *    `leavePlayer`-i isə onda `backToDetail` edir — tam ekrandan çıxış (Esc · brauzerin öz
 *    «tam ekrandan çıx»ı → `onFullscreenExit`), ✕, pultun/brauzerin «Geri»si istifadəçini heç
 *    açmadığı filmin MODALINA atırdı. Hero treylerinin «Tam ekran»ı bu qaydaya 2026-08-25-də
 *    keçmişdi (`app.ts` → `fromHero`), film isə keçməmişdi.
 *    NİYƏ ✕/«Geri» DƏ: veb TV-də Esc həm «geri», həm də tam ekran çıxışıdır və brauzer tam
 *    ekranda onu səhifəyə vermir — iki yolu ayırmaq olmur; hero treyleri də eyni cür işləyir.
 *    Modal və film səhifəsi yolu dəyişmir (`backToDetail`: modal açılır / səhifəyə qayıdılır).
 */
function launchPlayer(slug: string, found: ContentDetail | null, card: ContentCard | null, fromHero?: boolean): void {
  const id = found ? found.id : card ? card.id : ''
  if (!id) return
  const title = found ? found.title : card ? card.title : ''
  // 2026-10-25: serialda ən son yarımçıq bölüm — izah `lib/continue.ts` → `resumeEpisode`.
  const episode = found ? resumeEpisode(found.id, found.episodes) : null
  const ageLimit = found ? found.ageLimit : card ? card.ageLimit : null
  returnLimit = { slug, ageLimit }

  if (episode && found) {
    navigate({
      name: 'player',
      slug,
      title: episodeTitle(title, episode),
      contentId: id,
      episodeId: episode.id,
      episodes: found.episodes,
      ageLimit,
      fromHero,
    })
    return
  }
  // Hero kartının öz treyler mənbəyi var (`HeroItem.trailerUrl`) — detal yoxdursa ehtiyatdır.
  const cardTrailer = card ? (card as { trailerUrl?: string | null }).trailerUrl || null : null
  // Bilet alınmasa oynadıcı treylerə degrade edir — mənbə fallback kimi ötürülür.
  navigate({
    name: 'player',
    slug,
    title,
    contentId: id,
    trailerAsset: found ? found.trailerUrl : cardTrailer,
    // Sonluq qatının məlumatı — izah `app.ts`-dəki marşrut tipindədir.
    similar: found ? found.similar : undefined,
    outroSeconds: found ? found.outroSeconds : undefined,
    ageLimit,
    fromHero,
  })
}

/**
 * «SONRA İZLƏ» — siyahıya salır/çıxarır (2026-08-30, sahib tələbi).
 *
 * ⚠ ÇEVİRMƏ MƏNTİQİ `lib/watchlist.ts` → `toggleWatchlist`-dədir: eyni davranış
 *   dörd səthdədir və optimistik/geri-qaytarma qaydası bir gövdədə qalmalıdır.
 * ⚠ GİRİŞSİZ İSTİFADƏÇİ QR-a GEDİR — «İndi izlə» (`playFilm`) ilə EYNİ yol;
 *   əvvəlcə modal bağlanır ki, fon treyleri qalıq qoymasın.
 * ⚠ SESSİYA VAR, PROFİL SEÇİLMƏYİBSƏ «Kim baxır?»-a (2026-08-30) — cihaz
 *   modalındakı eyni qərar; izahı `lib/watchlist.ts`-dədir.
 */
function toggleLater(): void {
  const u = ui
  if (!u) return
  const found = detail
  const card = seed
  const id = found ? found.id : card ? card.id : ''
  if (!id) return
  toggleWatchlist(
    id,
    card ? card : found ? cardFromDetail(found) : null,
    () => paintLater(u),
    () => {
      closeWebDetail()
      navigate({ name: 'qr' })
    },
    () => {
      closeWebDetail()
      navigate({ name: 'profiles', replaceOnPick: false })
    },
  )
}

/**
 * Düymənin etiketini CARİ filmin vəziyyətinə görə yazır.
 *
 * ⚠ VƏZİYYƏT ARQUMENTLƏ GƏLMİR, İNDEKSDƏN OXUNUR: modal PAYLAŞILAN DOM-dur — sorğu
 *   uçarkən istifadəçi başqa filmə keçsə, bayrağı arqumentlə yazsaydıq geri
 *   qaytarma YENİ filmin düyməsini yanlış boyayardı (cihaz modalındakı eyni qeyd).
 */
function paintLater(u: WebModalUi): void {
  const found = detail
  const card = seed
  const id = found ? found.id : card ? card.id : ''
  setText(u.later, id && isInWatchlist(id) ? T('✓  Siyahımdadır') : T('+  Sonra İzlə'))
}

/**
 * TAM EKRAN — treyleri OYNADICI EKRANINDA açır (modalın içində böyütmür).
 *
 * ⚠ SƏBƏB CİHAZ MODALINDAKI İLƏ EYNİDİR (sahib bildirişi 2026-08-10): modalın
 *   fon treyleri İDARƏSİZ qatdır — orada nə oynat/dayandır, nə sürüşdürmə
 *   çubuğu, nə keyfiyyət menyusu var. Oynadıcı marşrutu isə tam dəsti verir.
 * ⚠ GERİ MODALA QAYTARIR: `backToDetail(slug)` → `setDetailOpener` (bax
 *   `browser/main.ts`).
 */
function openTrailerFullscreen(): void {
  const asset = upcomingCard ? upcomingCard.trailerUrl : detail ? detail.trailerUrl : null
  if (!asset) return
  const slug = currentSlug
  const title = upcomingCard ? upcomingCard.title : detail ? detail.title : seed ? seed.title : ''
  // 2026-10-25: tam ekran treyleri də `trailerAllowed`-dən keçir (düymə onsuz da gizlənir).
  const source = upcomingCard || detail || seed
  const ageLimit = source ? source.ageLimit : null
  if (!trailerAllowed(ageLimit)) return
  returnLimit = { slug, ageLimit }

  // ⚠ MÖVQE BAĞLANMADAN ƏVVƏL OXUNUR: `closeWebDetail()` `<video>`-nu dayandırır.
  const startAt = ui && ui.video && isFinite(ui.video.currentTime) ? ui.video.currentTime : 0

  closeWebDetail()
  navigate({ name: 'player', slug, title, trailerAsset: asset, trailerStartAt: startAt, ageLimit })
}

// ── Qabıq ──────────────────────────────────────────────────────────────────

function show(u: WebModalUi): void {
  if (!open) {
    sourceFocus = document.activeElement as HTMLElement | null
    open = true
    lockScroll()
    u.root.hidden = false
  }
  // Yeni film — pəncərə həmişə başdan oxunur.
  u.root.scrollTop = 0
  // ⚠ FOKUS MODALA KEÇİR: TV brauzerində pult fokusu ox düymələri ilə gəzdirir;
  //   fokus arxadakı səhifədə qalsaydı istifadəçi «pult işləmir» sanardı.
  focusFirst(u)
}

/**
 * Modalın İLK GÖRÜNƏN düyməsinə fokus verir.
 * ⚠ «Tezliklə» rejimində «İndi izlə» GİZLİDİR — gizli elementə `focus()` heç nə
 *   etmir və fokus arxadakı kartda qalardı (pult modalı idarə etməzdi).
 */
function focusFirst(u: WebModalUi): void {
  const target = !u.watch.hidden && !u.watch.disabled ? u.watch : !u.fullscreen.hidden ? u.fullscreen : u.close
  autoFocused = target
  target.focus()
}

/**
 * Detal gələndən sonra fokusu yenidən seçir — YALNIZ istifadəçi onu tərpətməyibsə və ya
 * fokuslu element artıq əlçatmazdırsa (söndü/gizləndi). İzah: `detailState` (2026-10-23).
 */
function refocus(u: WebModalUi): void {
  const active = document.activeElement as HTMLButtonElement | null
  const lost = !active || !u.root.contains(active) || active.disabled === true || active.hidden
  if (lost || active === autoFocused) focusFirst(u)
}

/**
 * Arxadakı sənədin sürüşməsini kilidləyir.
 *
 * ⚠ MÖVQE ƏL İLƏ SAXLANILIR VƏ GERİ QOYULUR: `overflow: hidden` bəzi
 *   mühərriklərdə `scrollTop`-u sıfırlayır — modalın bütün mənası isə arxadakı
 *   sıranın YERİNDƏ QALMASIDIR (M8).
 */
function lockScroll(): void {
  savedScroll = window.pageYOffset || document.documentElement.scrollTop || 0
  setClass(document.documentElement, 'is-dm-open', true)
  setClass(document.body, 'is-dm-open', true)
}

function unlockScroll(): void {
  setClass(document.documentElement, 'is-dm-open', false)
  setClass(document.body, 'is-dm-open', false)
  window.scrollTo(0, savedScroll)
}

function build(): WebModalUi {
  if (ui) return ui

  const root = el('div', 'web-dm')
  root.hidden = true

  const panel = el('div', 'web-dm__panel')
  root.appendChild(panel)

  // ── Üst hissə: treyler ──────────────────────────────────────────────────
  const hero = el('div', 'web-dm__hero')
  panel.appendChild(hero)

  const art = el('div', 'web-dm__art')
  hero.appendChild(art)

  const video = document.createElement('video')
  video.className = 'web-dm__trailer'
  video.muted = true
  video.setAttribute('playsinline', '')
  hero.appendChild(video)

  hero.appendChild(el('div', 'web-dm__scrim'))

  // Solda «İndi izlə», sağda tam ekran + səs (Android modalı ilə eyni bölgü).
  const actions = el('div', 'web-dm__actions')
  // ⚠ ETİKET AYRICA DÜYÜNDÜR: mətn «Tezliklə»yə dəyişəndə `setText` düymənin
  //   BÜTÜN uşaqlarını əvəz edərdi — play ikonu itərdi.
  const watch = webButton('', playFilm)
  const watchLabel = el('span', 'web-dm__btnlabel', T('İndi izlə'))
  watch.appendChild(iconBox(ICON_PLAY))
  watch.appendChild(watchLabel)
  actions.appendChild(watch)
  /*
    ⚠⚠ «SONRA İZLƏ» — 2026-08-30-da GERİ QAYTARILDI (sahib tələbi: «bütün tv
       projelərdə sonra izlə modulunu əlavə et»); 2026-08-04-dəki «düyməni
       yığışdır» qərarı artıq qüvvədə deyil.
    ⚠ `ghost` VARİANT: kadrın üstündə YALNIZ BİR dolu (CTA) düymə qalmalıdır —
      «İndi izlə». Cihaz modalındakı eyni bölgü.
  */
  const later = webButton(T('+  Sonra İzlə'), toggleLater, true)
  actions.appendChild(later)
  hero.appendChild(actions)

  const tools = el('div', 'web-dm__tools')
  const fullscreen = iconButton(ICON_FULLSCREEN, T('Tam ekran'), openTrailerFullscreen)
  const soundOff = iconBox(ICON_MUTED)
  const soundOn = iconBox(ICON_SOUND)
  soundOn.hidden = true
  const sound = iconButton('', T('Səs'), () => {
    const current = ui
    if (current) toggleSound(current)
  })
  sound.appendChild(soundOff)
  sound.appendChild(soundOn)
  tools.appendChild(fullscreen)
  tools.appendChild(sound)
  hero.appendChild(tools)

  // Sağ-yuxarıda «Bağla» — ikon + mətn (üç TV səthində eyni).
  const close = webButton(T('Bağla'), closeWebDetail, true)
  close.className += ' web-dm__close'
  close.insertBefore(iconBox(ICON_CLOSE), close.firstChild)
  /*
    ⚠ MƏTN DÜYÜNÜ AYRICA SAXLANILIR: `setText(close, …)` düymənin BÜTÜN uşaqlarını
      əvəz edərdi və ✕ ikonu itərdi (`watchLabel`-dəki eyni səbəb). İkon
      `insertBefore` ilə BAŞA salındığı üçün mətn həmişə SON uşaqdır.
  */
  const closeLabel = close.lastChild as Text
  hero.appendChild(close)

  // ── Ad · meta · təsvir ──────────────────────────────────────────────────
  const body = el('div', 'web-dm__body')
  const title = el('h2', 'web-dm__title')
  const meta = el('p', 'web-dm__meta')
  const metaText = el('span')
  const age = el('span', 'web-dm__age')
  age.hidden = true
  const tags = el('span', 'web-dm__tags')
  meta.appendChild(metaText)
  meta.appendChild(age)
  meta.appendChild(tags)
  const premiere = el('p', 'web-dm__premiere')
  premiere.hidden = true
  const desc = el('div', 'web-dm__desc')
  const crewToggle = document.createElement('details')
  const summary = document.createElement('summary')
  summary.textContent = T('Heyəti Gör') + ' ⌄'
  crewToggle.appendChild(summary)
  crewToggle.hidden = true
  const actors = el('p', 'web-dm__crew')
  actors.hidden = true
  crewToggle.appendChild(actors)
  const crew = el('p', 'web-dm__crew')
  crewToggle.appendChild(crew)
  crew.hidden = true
  body.appendChild(title)
  body.appendChild(meta)
  body.appendChild(premiere)
  body.appendChild(desc)
  body.appendChild(crewToggle)
  panel.appendChild(body)

  const similar = el('div', 'web-dm__similar')
  panel.appendChild(similar)

  /**
   * FON KLİKİ BAĞLAYIR — brauzerdə gözlənilən davranış.
   * ⚠ YALNIZ ÖRTÜYÜN ÖZÜ: panelin içindəki klik (kart, düymə, mətn seçimi)
   *   modalı bağlamamalıdır.
   */
  root.onclick = (event: MouseEvent) => {
    if (event.target === root) closeWebDetail()
  }

  /**
   * ESC BAĞLAYIR — və qabığın öz «geri» körpüsünə ÇATMIR.
   *
   * ⚠ `capture` MƏCBURİDİR: `browser/shell.ts` sənədə qoşulub və Esc-i `back()`
   *   kimi oxuyur; tutmasaq bir basış həm modalı bağlayar, həm də arxadakı
   *   ekranı dəyişərdi (istifadəçi iki addım geri getdiyini görər).
   */
  /*
    2026-10-23: «GERİ» DÜYMƏLƏRİ ORTAQ XƏRİTƏDƏN OXUNUR (`lib/keys.ts` → `toRemoteKey`).
      NƏ TAPILDI: bu dinləyici yalnız 27 (Esc) və 8 (Backspace) kodlarını tanıyırdı, qabıq isə
      461 (webOS Back) · 10009 (Tizen Return) · 166 (BrowserBack) və `key: 'BrowserBack'`-i
      də «geri» sayır.
      ƏVVƏL: webOS/Tizen pultunda «Geri» modalı BAĞLAMIRDI — hadisə qabığa çatırdı və
      arxadakı ekran dəyişirdi, modal isə açıq qalırdı.
      NİYƏ BELƏ: iki cədvəl yavaş-yavaş ayrılır; tək mənbə bunu struktur olaraq bağlayır.
    ⚠ YAŞ PƏNCƏRƏSİ (`.web-modal`) ÜSTDƏDİRSƏ bura çatmır: onun dinləyicisi `window`-un
      tutma fazasındadır və basışı udur (`browser/ui.ts` → `bindDialogKeys`).
  */
  document.addEventListener(
    'keydown',
    (event: KeyboardEvent) => {
      if (!open) return
      if (toRemoteKey(event) !== 'back') return
      const target = event.target as HTMLElement | null
      const tag = target && target.tagName ? target.tagName.toUpperCase() : ''
      if (tag === 'INPUT' || tag === 'TEXTAREA') return
      event.preventDefault()
      event.stopPropagation()
      closeWebDetail()
    },
    true,
  )

  /**
   * FOKUS TƏLƏSİ — modal açıq olanda fokus arxadakı səhifəyə keçə bilməz.
   *
   * ⚠ NİYƏ LAZIMDIR: TV brauzerində ox düymələri BRAUZERİN öz fokus
   *   naviqasiyasıdır (`lib/platform.ts`) — tələ olmasa pult modalın altındakı
   *   kartlara «düşür» və ekranda görünməyən elementə basılır.
   */
  document.addEventListener(
    'focusin',
    (event: Event) => {
      const current = ui
      if (!open || !current) return
      // ⚠ YAŞ QAPISI İSTİSNADIR: oxşar kartdan 18+ film seçiləndə onun öz
      //   pəncərəsi (`.web-modal`) açılır və fokus ORA getməlidir — geri
      //   qaytarsaydıq həmin pəncərənin düymələri pultla əlçatmaz qalardı.
      if (document.querySelector('.web-modal')) return
      const target = event.target as Node | null
      if (target && current.root.contains(target)) return
      focusFirst(current)
    },
    false,
  )

  document.body.appendChild(root)

  ui = {
    root,
    panel,
    art,
    video,
    actions,
    watch,

    watchLabel,
    later,
    fullscreen,
    sound,
    soundOn,
    soundOff,
    close,
    closeLabel,
    title,
    metaText,
    age,
    tags,
    premiere,
    desc,
    crew,
    actors,
    crewToggle,
    similar,
  }
  return ui
}

